import { getColorMode, type UserWithConfig } from '@ordo/auth'
import { syncSharedColorMode } from '@ordo/ui/shared-color-mode'
import { type ColorMode, useColorMode } from '@ordo/ui/use-dark-mode'

/** Boombox's long-standing key. NOT renamed to the shared default:
 *  cross-app sync happens through the shared `.ordo.com` cookie, so a
 *  rename would only reset every existing user's stored choice. The
 *  pre-paint script in `index.html` falls back to the same key. */
const STORAGE_KEY = 'comms-theme'

export type ThemePreference = ColorMode

/**
 * Sync the theme once the account preference (`getColorMode(user)` from
 * multipass) is known. The choice shared by every internal app wins, then
 * the account, then this app's own stored value. Idempotent and safe to
 * call repeatedly as auth hydrates. Does NOT write localStorage.
 */
export function applyDefaultThemePreference(preference: ThemePreference | null | undefined) {
  syncSharedColorMode(STORAGE_KEY, preference)
}

/**
 * Three-way color mode for this app. Thin wrapper over the shared
 * `useColorMode` that pins boombox's storage key; `setPreference` also
 * persists the choice to the user's multipass account (PROD-986) via
 * `/api/auth/me/config/color_mode`.
 *
 * Pass the signed-in user so the control reports the account preference
 * on a device that has never toggled here — otherwise it reads 'system'
 * while `applyDefaultThemePreference` has already painted the account
 * value. The user is a parameter rather than a `useAuthStore` read
 * because `stores/auth` imports this module, and importing it back would
 * cycle.
 */
export function useTheme(user?: UserWithConfig | null) {
  const { preference, resolved, setPreference } = useColorMode({
    storageKey: STORAGE_KEY,
    fallback: getColorMode(user) ?? null,
    shared: true,
  })

  return { theme: resolved, preference, setPreference } as const
}
